import React, { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { ArenaParticipant } from "@/hooks/useLiveArenas";
import { JoinRequest } from "@/hooks/useBoothJoinRequests";
import { 
  Crown, 
  Mic, 
  MicOff, 
  Video, 
  VideoOff, 
  UserX, 
  Ban,
  StopCircle,
  Users,
  UserPlus,
  Check,
  X,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";

// Video thumbnail component for participant streams
const VideoThumbnail: React.FC<{ stream?: MediaStream | null; fallbackUrl?: string; name?: string }> = ({
  stream,
  fallbackUrl,
  name,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [hasVideo, setHasVideo] = useState(false);

  useEffect(() => {
    if (videoRef.current && stream) {
      const videoTrack = stream.getVideoTracks()[0];
      if (videoTrack && videoTrack.enabled && videoTrack.readyState !== "ended") {
        videoRef.current.srcObject = stream;
        setHasVideo(true);
      } else {
        setHasVideo(false);
      }
    } else {
      setHasVideo(false);
    }
  }, [stream]);

  if (hasVideo && stream) {
    return (
      <div className="w-8 h-8 rounded overflow-hidden bg-muted flex-shrink-0">
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className="w-full h-full object-cover"
          style={{ transform: "scaleX(-1)" }}
        />
      </div>
    );
  }

  return (
    <Avatar className="w-8 h-8 flex-shrink-0">
      <AvatarImage src={fallbackUrl} />
      <AvatarFallback className="text-xs">
        {(name || "U").charAt(0)}
      </AvatarFallback>
    </Avatar>
  );
};

interface BoothAdminDropdownProps {
  participants: ArenaParticipant[];
  pendingRequests?: JoinRequest[];
  currentUserId?: string;
  participantStreams?: Map<string, MediaStream>; // Map of userId -> stream
  localStream?: MediaStream | null;
  isUserMuted: (userId: string) => boolean;
  isUserCameraDisabled: (userId: string) => boolean;
  onMute: (userId: string) => void;
  onUnmute: (userId: string) => void;
  onDisableCamera: (userId: string) => void;
  onEnableCamera: (userId: string) => void;
  onKick: (userId: string, reason?: string) => void;
  onBan: (userId: string, reason?: string) => void;
  onAdmitUser?: (requestId: string) => void;
  onRejectRequest?: (requestId: string) => void;
  onEndBooth: () => void;
}

const BoothAdminDropdown: React.FC<BoothAdminDropdownProps> = ({
  participants,
  pendingRequests = [],
  currentUserId,
  participantStreams = new Map(),
  localStream,
  isUserMuted,
  isUserCameraDisabled,
  onMute,
  onUnmute,
  onDisableCamera,
  onEnableCamera,
  onKick,
  onBan,
  onAdmitUser,
  onRejectRequest,
  onEndBooth,
}) => {
  const [confirmKick, setConfirmKick] = useState<ArenaParticipant | null>(null);
  const [confirmBan, setConfirmBan] = useState<ArenaParticipant | null>(null);
  const [confirmEnd, setConfirmEnd] = useState(false);

  const otherParticipants = participants.filter(p => p.user_id !== currentUserId);
  const hasPendingRequests = pendingRequests.length > 0;

  // Get stream for a participant
  const getParticipantStream = (userId: string) => {
    if (userId === currentUserId) return localStream;
    return participantStreams.get(userId);
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 gap-1.5 text-amber-500 hover:text-amber-600 hover:bg-amber-500/10"
          >
            <Crown className="w-3.5 h-3.5" />
            <span className="text-xs font-medium">Admin</span>
            {hasPendingRequests && (
              <Badge variant="destructive" className="h-4 w-4 p-0 text-[10px] flex items-center justify-center rounded-full">
                {pendingRequests.length}
              </Badge>
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-72" sideOffset={8}>
          <DropdownMenuLabel className="flex items-center gap-2 text-amber-500">
            <Crown className="w-4 h-4" />
            Admin Controls
          </DropdownMenuLabel>
          <DropdownMenuSeparator />

          {/* Admit Users - Pending Requests */}
          {hasPendingRequests ? (
            <DropdownMenuSub>
              <DropdownMenuSubTrigger className="flex items-center gap-2">
                <UserPlus className="w-4 h-4" />
                <span>Admit Users</span>
                <Badge variant="secondary" className="ml-auto h-5 px-1.5 text-xs">
                  {pendingRequests.length}
                </Badge>
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="w-64">
                {pendingRequests.map((request) => (
                  <div key={request.id} className="px-2 py-2 flex items-center gap-2">
                    <Avatar className="w-6 h-6 flex-shrink-0">
                      <AvatarImage src={request.profile?.photo_url} />
                      <AvatarFallback className="text-xs">
                        {(request.profile?.name || "U").charAt(0)}
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-sm truncate flex-1">
                      {request.profile?.name || "User"}
                    </span>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 text-green-500 hover:text-green-600 hover:bg-green-500/10"
                        onClick={() => onAdmitUser?.(request.id)}
                        title="Admit"
                      >
                        <Check className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 text-red-500 hover:text-red-600 hover:bg-red-500/10"
                        onClick={() => onRejectRequest?.(request.id)}
                        title="Reject"
                      >
                        <X className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                ))}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          ) : (
            <DropdownMenuItem disabled className="flex items-center gap-2 opacity-50">
              <UserPlus className="w-4 h-4" />
              <span>No pending requests</span>
            </DropdownMenuItem>
          )}

          <DropdownMenuSeparator />

          {/* Manage Participants */}
          {otherParticipants.length === 0 ? (
            <div className="px-2 py-3 text-sm text-muted-foreground text-center">
              <Users className="w-4 h-4 mx-auto mb-1 opacity-50" />
              No other participants yet
            </div>
          ) : (
            <DropdownMenuSub>
              <DropdownMenuSubTrigger className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                <span>Manage Participants</span>
                <Badge variant="outline" className="ml-auto h-5 px-1.5 text-xs">
                  {otherParticipants.length}
                </Badge>
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="w-72">
                {otherParticipants.map((participant, index) => {
                  const isMuted = isUserMuted(participant.user_id);
                  const isCameraOff = isUserCameraDisabled(participant.user_id);
                  const stream = getParticipantStream(participant.user_id);

                  return (
                    <div key={participant.user_id} className="px-2 py-2">
                      <div className="flex items-center gap-2 mb-2">
                        {/* Video thumbnail or avatar */}
                        <VideoThumbnail
                          stream={stream}
                          fallbackUrl={participant.profile?.photo_url}
                          name={participant.profile?.name}
                        />
                        <span className="text-sm font-medium truncate flex-1">
                          {participant.profile?.name || "User"}
                        </span>
                        <Badge variant="outline" className="h-5 px-1.5 text-[10px]">
                          🎈 #{index + 2}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-1 ml-10">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => isMuted ? onUnmute(participant.user_id) : onMute(participant.user_id)}
                          title={isMuted ? "Unmute" : "Mute"}
                        >
                          {isMuted ? <MicOff className="w-3.5 h-3.5 text-red-500" /> : <Mic className="w-3.5 h-3.5" />}
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => isCameraOff ? onEnableCamera(participant.user_id) : onDisableCamera(participant.user_id)}
                          title={isCameraOff ? "Enable camera" : "Disable camera"}
                        >
                          {isCameraOff ? <VideoOff className="w-3.5 h-3.5 text-red-500" /> : <Video className="w-3.5 h-3.5" />}
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-orange-500 hover:text-orange-600 hover:bg-orange-500/10"
                          onClick={() => setConfirmKick(participant)}
                          title="Kick from booth"
                        >
                          <UserX className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-destructive hover:text-destructive hover:bg-destructive/10"
                          onClick={() => setConfirmBan(participant)}
                          title="Ban from booth"
                        >
                          <Ban className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                      {index < otherParticipants.length - 1 && (
                        <DropdownMenuSeparator className="mt-2" />
                      )}
                    </div>
                  );
                })}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          )}

          <DropdownMenuSeparator />

          {/* End Booth */}
          <DropdownMenuItem 
            className="text-destructive focus:text-destructive focus:bg-destructive/10 cursor-pointer"
            onClick={() => setConfirmEnd(true)}
          >
            <StopCircle className="w-4 h-4 mr-2" />
            End Booth for Everyone
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Confirm Kick Dialog */}
      <AlertDialog open={!!confirmKick} onOpenChange={() => setConfirmKick(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <UserX className="w-5 h-5 text-orange-500" />
              Kick {confirmKick?.profile?.name}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will remove them from the booth. They <strong>can rejoin</strong> later unless you ban them.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-orange-500 text-white hover:bg-orange-600"
              onClick={() => {
                if (confirmKick) {
                  onKick(confirmKick.user_id, "Removed by booth admin");
                  setConfirmKick(null);
                }
              }}
            >
              Kick
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirm Ban Dialog */}
      <AlertDialog open={!!confirmBan} onOpenChange={() => setConfirmBan(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-destructive">
              <Ban className="w-5 h-5" />
              Ban {confirmBan?.profile?.name}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will remove them from the booth and <strong>prevent them from rejoining</strong> this booth session.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (confirmBan) {
                  onBan(confirmBan.user_id, "Banned by booth admin");
                  setConfirmBan(null);
                }
              }}
            >
              Ban User
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirm End Booth Dialog */}
      <AlertDialog open={confirmEnd} onOpenChange={setConfirmEnd}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-destructive">
              <StopCircle className="w-5 h-5" />
              End this booth?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will end the booth for <strong>all participants</strong>. Everyone will be disconnected immediately.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                onEndBooth();
                setConfirmEnd(false);
              }}
            >
              End Booth
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default BoothAdminDropdown;
